import DOMPurify from "dompurify";
import { Mail } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { BACKEND } from "@/lib/api";
import AttachmentCard from "@/components/AttachmentCard";

const TOKEN_RE = /\[\[(img|video|file):([^\]]+)\]\]/g;

// Consenti solo iframe da host video fidati (i contenuti li scrive l'admin)
const ALLOWED_IFRAME_HOSTS = ["www.youtube.com", "youtube.com", "www.youtube-nocookie.com", "player.vimeo.com"];
if (typeof window !== "undefined" && !window.__articleVideoHook) {
  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName === "iframe") {
      const src = node.getAttribute("src") || "";
      let ok = false;
      try { ok = ALLOWED_IFRAME_HOSTS.includes(new URL(src).hostname); } catch { ok = false; }
      if (!ok) node.parentNode?.removeChild(node);
    }
  });
  window.__articleVideoHook = true;
}
const SANITIZE_OPTS = { ADD_TAGS: ["iframe"], ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "target", "controls", "controlslist", "playsinline", "download", "rel"] };

const ytId = (url) => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

function MediaBlock({ kind, url }) {
  if (kind === "file") {
    const [src, name] = url.split("|");
    return <AttachmentCard url={src.trim()} name={(name || "").trim() || decodeURIComponent(src.split("/").pop())} testid="article-media-file" />;
  }
  if (kind === "img") {
    return <img src={mediaUrl(url)} alt="" className="w-full rounded-lg border border-white/10 my-3" data-testid="article-media-img" />;
  }
  const yt = ytId(url);
  if (yt) {
    return (
      <div className="relative w-full aspect-video my-3 rounded-lg overflow-hidden border border-white/10">
        <iframe title="video" data-testid="article-media-video" src={`https://www.youtube.com/embed/${yt}`} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return <video src={mediaUrl(url)} controls data-testid="article-media-video" className="w-full rounded-lg border border-white/10 my-3" />;
}

function TokenRender({ src }) {
  const parts = [];
  let last = 0;
  let m;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) parts.push({ t: "text", v: src.slice(last, m.index) });
    parts.push({ t: "media", kind: m[1], url: m[2].trim() });
    last = m.index + m[0].length;
  }
  if (last < src.length) parts.push({ t: "text", v: src.slice(last) });
  return parts.map((p, i) =>
    p.t === "text"
      ? p.v.trim() && <p key={i} className="whitespace-pre-line leading-relaxed break-words">{p.v}</p>
      : <MediaBlock key={i} kind={p.kind} url={p.url} />
  );
}

function NewsletterCta() {
  return (
    <a href="/#newsletter" data-testid="article-newsletter-cta"
      className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-sm font-semibold text-gold hover:text-[#FDE047] transition-colors">
      <Mail className="w-4 h-4 shrink-0" /> Iscriviti alla newsletter per non perderti le novità →
    </a>
  );
}

const isHtml = (s) => /<([a-z][\s\S]*?)>/i.test(s);

// Gli allegati inseriti dall'editor (a.article-attachment) vengono resi come schede React (anteprima PDF, Apri/Scarica)
const ATTACHMENT_RE = /<a\b[^>]*class="[^"]*article-attachment[^"]*"[^>]*>[\s\S]*?<\/a>/g;
const attr = (html, name) => { const m = html.match(new RegExp(`${name}="([^"]*)"`)); return m ? m[1].replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">") : ""; };

function HtmlWithAttachments({ html }) {
  const parts = [];
  let last = 0;
  let m;
  ATTACHMENT_RE.lastIndex = 0;
  while ((m = ATTACHMENT_RE.exec(html)) !== null) {
    if (m.index > last) parts.push({ t: "html", v: html.slice(last, m.index) });
    parts.push({ t: "att", url: attr(m[0], "href"), name: attr(m[0], "data-name"), size: Number(attr(m[0], "data-size")) || undefined });
    last = m.index + m[0].length;
  }
  if (last < html.length) parts.push({ t: "html", v: html.slice(last) });
  return parts.map((p, i) =>
    p.t === "html"
      ? <div key={i} className="article-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.v, SANITIZE_OPTS) }} />
      : <AttachmentCard key={i} url={p.url} name={p.name || decodeURIComponent((p.url || "").split("/").pop() || "Allegato")} size={p.size} testid={`article-attachment-${i}`} />
  );
}

export default function ArticleBody({ text, className = "", showNewsletterCta = false }) {
  const src = text || "";
  // BlotFormatter2 usa la CSS var --resize-width per la larghezza degli allineamenti;
  // la convertiamo in width standard così sopravvive alla sanificazione e al render pubblico.
  // Inoltre riscriviamo eventuali URL immagine/media con dominio preview obsoleto al backend corrente.
  const prepared = src
    .replace(/--resize-width\s*:/g, "width:")
    .replace(/https?:\/\/[^"'\s)]+?(\/api\/(?:image|media)\/[^"'\s)]+)/g, `${BACKEND}$1`);
  return (
    <div className={className} data-testid="article-body">
      {isHtml(src)
        ? <HtmlWithAttachments html={prepared} />
        : <TokenRender src={src} />}
      {showNewsletterCta && <NewsletterCta />}
    </div>
  );
}

export { isHtml };
